import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, Clock, Sparkles } from "lucide-react";

/**
 * FeedbackForm — shared between /support (kind="support") and /verbesserung
 * (kind="improvement"). Posts to the FastAPI /feedback router and renders a
 * history of previous submissions for the current user.
 */
export default function FeedbackForm({
  kind,                 // "support" | "improvement"
  title,
  subtitle,
  hero,                 // { icon, title, description, gradient }
  topics,               // string[] suggested subjects
  emptyState,           // text to show when no items yet
  showPriority = true,
  testidPrefix = "fb",
}) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadItems = async () => {
    try {
      const { data } = await api.get(`/feedback/me?kind=${kind}`);
      setItems(data.items || []);
    } catch (e) {
      // silent — user might not have any yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); /* eslint-disable-next-line */ }, [kind]);

  const submit = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Bitte Betreff und Nachricht ausfüllen.");
    setSubmitting(true);
    try {
      await api.post("/feedback", { kind, subject, message, priority });
      toast.success(kind === "support" ? "Anfrage abgeschickt — wir melden uns!" : "Vielen Dank für deinen Vorschlag!");
      setSubject("");
      setMessage("");
      setPriority("normal");
      loadItems();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const HeroIcon = hero.icon;

  return (
    <Layout title={title} subtitle={subtitle}>
      <div className="max-w-5xl space-y-8">
        {/* Hero */}
        <div className={`relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br ${hero.gradient} text-white p-8`} data-testid={`${testidPrefix}-hero`}>
          <div className="absolute right-6 top-6 opacity-30"><HeroIcon className="h-24 w-24" /></div>
          <Badge className="bg-white/20 backdrop-blur border-white/30 text-white mb-3">
            <Sparkles className="h-3 w-3 mr-1" /> {hero.title}
          </Badge>
          <h2 className="font-display text-3xl font-bold tracking-tight max-w-2xl">{hero.headline}</h2>
          <p className="mt-2 text-white/90 max-w-xl">{hero.description}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <Card className="lg:col-span-2 p-6">
            <h3 className="font-display text-lg font-bold tracking-tight mb-4">Neuer Eintrag</h3>
            <div className="space-y-5">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Betreff</Label>
                <Input
                  className="mt-1.5"
                  placeholder={kind === "support" ? "z. B. PDF-Export schlägt fehl" : "z. B. Sammel-Export aller Akten als ZIP"}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid={`${testidPrefix}-subject`}
                />
                {topics?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {topics.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setSubject(t)}
                        className="text-[11px] font-medium px-2 py-1 rounded-md bg-zinc-100 hover:bg-emerald-50 hover:text-emerald-700 text-zinc-600 transition"
                        data-testid={`${testidPrefix}-suggest-${t.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {showPriority && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Priorität</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="mt-1.5" data-testid={`${testidPrefix}-priority`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Niedrig — kein Zeitdruck</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">Hoch — blockiert mich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Nachricht</Label>
                <Textarea
                  className="mt-1.5 min-h-[140px]"
                  placeholder={kind === "support"
                    ? "Beschreibe das Problem so genau wie möglich. Wenn möglich Schritte zur Reproduktion + Aktennummer."
                    : "Was würde dir den Alltag erleichtern? Welche Funktion fehlt? Je konkreter, desto besser können wir helfen."}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid={`${testidPrefix}-message`}
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={submit} disabled={submitting} data-testid={`${testidPrefix}-submit`}>
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Absenden
                </Button>
              </div>
            </div>
          </Card>

          {/* History */}
          <Card className="p-6">
            <h3 className="font-display text-lg font-bold tracking-tight mb-4">
              {kind === "support" ? "Deine Anfragen" : "Deine Vorschläge"}
            </h3>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 text-sm text-zinc-400" data-testid={`${testidPrefix}-empty`}>
                {emptyState || "Noch keine Einträge."}
              </div>
            ) : (
              <ul className="space-y-3" data-testid={`${testidPrefix}-list`}>
                {items.map((it) => (
                  <li key={it.id} className="rounded-lg border border-zinc-100 p-3" data-testid={`${testidPrefix}-item-${it.id}`}>
                    <div className="flex items-start gap-2">
                      <StatusBadge status={it.status} />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-sm truncate">{it.subject}</div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">
                          {new Date(it.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          {it.priority === "high" && <> · <span className="text-rose-600 font-semibold">Hoch</span></>}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-600 mt-2 line-clamp-3">{it.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

function StatusBadge({ status }) {
  const map = {
    open:        { Icon: Clock,          cls: "bg-amber-50 text-amber-700 border-amber-200",     label: "Offen" },
    in_progress: { Icon: Sparkles,       cls: "bg-cyan-50 text-cyan-700 border-cyan-200",        label: "In Arbeit" },
    resolved:    { Icon: CheckCircle2,   cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Erledigt" },
  };
  const m = map[status] || map.open;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold border rounded-full px-1.5 py-0.5 shrink-0 ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}
